import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress, Divider, Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

const buildRewardUpdateErrorMessage = (status, detail) => {
    const fallback = detail || `Erreur HTTP ${status || "inconnu"}`;
    if (status !== 409) return fallback;

    const text = String(detail || "").toLowerCase();
    if (text.includes("catégorie") || text.includes("categorie") || text.includes("reward_category_id")) {
        return "La catégorie sélectionnée est invalide pour cette marque. Rechargez la liste des catégories et réessayez.";
    }
    if (text.includes("identique") || text.includes("existe déjà") || text.includes("existe deja")) {
        return "Une récompense similaire existe déjà. Modifiez le nom (ou la description) puis réessayez.";
    }
    return "Conflit de données côté serveur pendant la mise à jour. Vérifiez les valeurs saisies; si le problème persiste via API aussi, le schéma SQL de la table rewards doit être vérifié sur le serveur.";
};

const EditRewardDialog = ({ open, reward, onClose, onSaved, rewardCategories = [] }) => {
    const [form, setForm] = useState({ name: "", description: "", reward_category_id: "", active: true });
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productLines, setProductLines] = useState([{ product_id: "", quantity: 1 }]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (reward) {
            setForm({
                name: reward.name || "",
                description: reward.description || "",
                reward_category_id: reward.reward_category_id || "",
                active: reward.active ?? true,
            });
            const existing = Array.isArray(reward.products) ? reward.products : [];
            setProductLines(
                existing.length > 0
                    ? existing.map((p) => ({ product_id: String(p.product_id), quantity: Number(p.quantity || 1) }))
                    : [{ product_id: "", quantity: 1 }]
            );
            setError(null);
        }
    }, [reward]);

    useEffect(() => {
        if (!open || !reward?.brand) return;
        let cancelled = false;
        setLoadingProducts(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/products?active=true&brand=${encodeURIComponent(reward.brand)}`, {
            headers: { "X-Brand": reward.brand },
        })
            .then(async (res) => {
                const data = await res.json().catch(() => null);
                if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
                if (!cancelled) setProducts(Array.isArray(data) ? data : []);
            })
            .catch(() => { if (!cancelled) setProducts([]); })
            .finally(() => { if (!cancelled) setLoadingProducts(false); });
        return () => { cancelled = true; };
    }, [open, reward?.brand]);

    const productById = useMemo(() => {
        const m = {};
        products.forEach((p) => { m[String(p.id)] = p; });
        return m;
    }, [products]);

    const selectedProductIds = useMemo(
        () => new Set(productLines.map((l) => String(l.product_id || "")).filter(Boolean)),
        [productLines]
    );

    const addProductLine = () => setProductLines((prev) => [...prev, { product_id: "", quantity: 1 }]);
    const removeProductLine = (idx) => setProductLines((prev) => prev.filter((_, i) => i !== idx));
    const updateProductLine = (idx, patch) =>
        setProductLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

    const handleChange = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const isValid = form.name.trim();

    const handleSubmit = async () => {
        if (!isValid || !reward) return;
        setSubmitting(true);
        setError(null);
        try {
            const cleanedProducts = productLines
                .map((l) => ({
                    product_id: l.product_id || null,
                    quantity: Number(l.quantity || 1),
                }))
                .filter((l) => !!l.product_id);
            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                reward_category_id: form.reward_category_id || null,
                active: form.active,
                products: cleanedProducts.length > 0 ? cleanedProducts : [],
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rewards/${reward.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "X-Brand": reward.brand },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(buildRewardUpdateErrorMessage(res.status, e?.detail));
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!reward) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier la Récompense
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{reward.brand} • {reward.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom" value={form.name}
                            onChange={handleChange("name")} size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handleChange("description")} size="small" multiline rows={2}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth select label="Catégorie de Récompense (optionnel)"
                            value={form.reward_category_id} onChange={handleChange("reward_category_id")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                            <MenuItem value=""><em>Aucune catégorie</em></MenuItem>
                            {rewardCategories.map((rc) => (
                                <MenuItem key={rc.id} value={rc.id}>{rc.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Actif</Typography>
                            <Switch checked={form.active}
                                onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main },
                                }} />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box mt={1}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography fontWeight={800} fontFamily="Montserrat, sans-serif" color="#1a1a2e" fontSize={13}>
                                    Produits associés (optionnel)
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={addProductLine}
                                    disabled={submitting}
                                    sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
                                >
                                    Ajouter un produit
                                </Button>
                            </Box>

                            {loadingProducts ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={16} />
                                    <Typography fontSize={12} color="text.secondary">Chargement des produits…</Typography>
                                </Box>
                            ) : products.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Aucun produit disponible. Créez d’abord un produit dans le catalogue.
                                </Alert>
                            ) : (
                                <Grid container spacing={1.5}>
                                    {productLines.map((line, idx) => {
                                        const pid = line.product_id ? String(line.product_id) : "";
                                        const prod = pid ? productById[pid] : null;
                                        return (
                                            <React.Fragment key={idx}>
                                                <Grid item xs={12} sm={7}>
                                                    <TextField
                                                        fullWidth
                                                        select
                                                        label="Produit"
                                                        value={pid}
                                                        onChange={(e) => updateProductLine(idx, { product_id: e.target.value })}
                                                        size="small"
                                                        disabled={submitting}
                                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                        helperText={prod?.points_value != null ? `Valeur: ${prod.points_value} point(s)` : " "}
                                                    >
                                                        <MenuItem value=""><em>Sélectionner…</em></MenuItem>
                                                        {products.map((p) => {
                                                            const idStr = String(p.id);
                                                            const taken = selectedProductIds.has(idStr) && idStr !== pid;
                                                            return (
                                                                <MenuItem key={idStr} value={idStr} disabled={taken}>
                                                                    {p.name}{taken ? " (déjà ajouté)" : ""}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={8} sm={3}>
                                                    <TextField
                                                        fullWidth
                                                        label="Quantité"
                                                        type="number"
                                                        value={line.quantity}
                                                        onChange={(e) => updateProductLine(idx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                                                        size="small"
                                                        disabled={submitting}
                                                        InputProps={{ inputProps: { min: 1 } }}
                                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                    />
                                                </Grid>
                                                <Grid item xs={4} sm={2} display="flex" alignItems="center" justifyContent="flex-end">
                                                    <IconButton
                                                        onClick={() => removeProductLine(idx)}
                                                        disabled={submitting || productLines.length === 1}
                                                        size="small"
                                                        sx={{ mt: { xs: 0.5, sm: 0 } }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Grid>
                                                {pid && prod?.match_key && (
                                                    <Grid item xs={12}>
                                                        <Box display="flex" gap={1} flexWrap="wrap">
                                                            <Chip size="small" label={`match_key: ${prod.match_key}`} variant="outlined" />
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRewardDialog;
